import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import z from "zod";

import { useFoodpilotContext } from "src/context/FoodpilotContext";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import {
    MainScoreSchema,
    ScoreSchema,
    VariationSchema,
    type MainScore,
    type Variation,
} from "src/api/client-api/olapScores/olapScoresSchema";

export const calculatePackagingPreviewKey = "calculatePackagingPreview" as const;

export type PackagingPreviewInput = {
    type: "element" | "composite";
    campaignId?: number | null;
    packagingReferenceId?: number | null;
    packagingReferenceCompositeId?: number | null;
    weight?: number | null;
    recycled?: boolean | null;
    recycledProportion?: number | null;
    recyclable?: boolean | null;
    reuse?: boolean | null;
    composition?: Array<{ childPackagingId: number; amount: number; isMain?: boolean }>;
};

export type PackagingPreviewVariation = Variation & {
    score: { value: number | null };
};

export type PackagingPreviewScore = Omit<MainScore, "variations"> & {
    variations: PackagingPreviewVariation[];
};

export type PackagingPreviewScores = Record<number, PackagingPreviewScore>;

const PackagingPreviewVariationSchema = VariationSchema.extend({
    score: ScoreSchema,
});
const PackagingPreviewScoreSchema = MainScoreSchema.extend({
    variations: z.array(PackagingPreviewVariationSchema),
});
const PackagingPreviewScoresSchema = z.record(z.string(), PackagingPreviewScoreSchema);
const LegacyEmptyPackagingPreviewScoresSchema = z.tuple([]).transform(() => ({}));
const PackagingPreviewResponseSchema = z.object({
    olapScores: z.union([PackagingPreviewScoresSchema, LegacyEmptyPackagingPreviewScoresSchema]),
});

export const parsePackagingPreviewScores = (data: unknown): PackagingPreviewScores =>
    zodParseWithLog(data, PackagingPreviewResponseSchema, calculatePackagingPreviewKey)
        .olapScores as PackagingPreviewScores;

const useDebouncedValue = <T>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedValue(value), delay);

        return () => window.clearTimeout(timeout);
    }, [delay, value]);

    return debouncedValue;
};

type Options = {
    enabled?: boolean;
    debounceMs?: number;
};

export const useCalculatePackagingPreview = (input: PackagingPreviewInput, options: Options = {}) => {
    const { enabled = true, debounceMs = 350 } = options;
    const { currentCampaign } = useFoodpilotContext();
    const inputWithCampaign = useMemo(
        () => ({ ...input, campaignId: currentCampaign?.id ?? null }),
        [currentCampaign?.id, input],
    );
    const debouncedInput = useDebouncedValue(inputWithCampaign, debounceMs);
    const stableInput = useMemo(() => debouncedInput, [debouncedInput]);

    return useQuery({
        queryKey: [calculatePackagingPreviewKey, stableInput],
        enabled,
        keepPreviousData: true,
        staleTime: 0,
        cacheTime: 0,
        queryFn: async () => loggedInApiCall.post("/api/packagings/calculate-preview", stableInput),
        select: (request) => parsePackagingPreviewScores(request.data),
    });
};
